import React, { useState, useEffect } from 'react';
import { 
  Chart as ChartJS, 
  CategoryScale, 
  LinearScale, 
  BarElement, 
  LineElement,
  PointElement,
  Title, 
  Tooltip, 
  Legend 
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';
import axios from 'axios';
import '../styles/SalesCharts.css';

// Registrar componentes de Chart.js
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const SellerSalesChart = ({ sellerId }) => {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSellerSalesData = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        
        console.log('Obteniendo datos de ventas para el vendedor:', sellerId);
        
        // Obtener el mes y año actual
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1; // JavaScript months are 0-indexed
        
        // Obtener el primer y último día del mes actual
        const firstDayOfMonth = new Date(currentYear, currentMonth - 1, 1);
        const lastDayOfMonth = new Date(currentYear, currentMonth, 0);
        const daysInMonth = lastDayOfMonth.getDate();
        
        // Crear array de etiquetas para cada día del mes
        const labels = Array.from({ length: daysInMonth }, (_, i) => `Día ${i + 1}`);
        
        // Hacer la solicitud al nuevo endpoint específico para ventas diarias del vendedor
        const response = await axios.get('/api/seller-daily-sales/daily-chart', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        
        setChartData(response.data);
        setError(null);
      } catch (error) {
        console.error('Error al obtener datos de ventas del vendedor:', error);
        
        // Crear datos vacíos para el mes actual
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;
        const lastDay = new Date(currentYear, currentMonth, 0).getDate();
        
        // Crear etiquetas para cada día del mes
        const labels = Array.from({ length: lastDay }, (_, i) => `Día ${i + 1}`);
        
        // Crear arrays de datos vacíos
        const dailyTotals = Array(lastDay).fill(0);
        const cumulativeTotals = Array(lastDay).fill(0);
        
        // Crear datos de gráfica vacíos
        const emptyChartData = {
          labels,
          year: currentYear,
          month: currentMonth,
          monthlyData: {
            totals: dailyTotals
          },
          cumulativeData: {
            totals: cumulativeTotals
          }
        };
        
        setChartData(emptyChartData);
        setError('No hay datos de ventas para mostrar');
      } finally {
        setLoading(false);
      }
    };

    if (sellerId) {
      fetchSellerSalesData();
    }
  }, [sellerId]);

  if (loading) {
    return <div className="charts-loading">Cargando gráficas...</div>;
  }

  if (error) {
    return <div className="charts-error">{error}</div>;
  }

  if (!chartData) {
    return <div className="charts-empty">No hay datos disponibles para las gráficas</div>;
  }

  // Obtener el nombre del mes actual
  const getMonthName = (monthNumber) => {
    const months = [
      'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
    ];
    return months[monthNumber - 1];
  };

  const monthName = getMonthName(chartData.month);
  
  // Configuración para la gráfica de barras (ventas diarias)
  const barChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: `Ventas Diarias de ${monthName} ${chartData.year} (Mis Ventas)`,
      },
    },
  };

  const barChartData = {
    labels: chartData.labels,
    datasets: [
      {
        label: 'Ventas Diarias',
        data: chartData.monthlyData.totals,
        backgroundColor: 'rgba(53, 162, 235, 0.5)',
      },
    ],
  };

  // Definir el color principal para la gráfica de ventas mensuales (Mayo)
  const monthlyChartMainColor = 'rgba(153, 102, 255, 0.7)';
  
  // Configuración para la gráfica de barras (ventas mensuales)
  const monthlyChartOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          // Modificar el color del cuadrado en la leyenda para que coincida con Mayo
          usePointStyle: false,
          boxWidth: 10,
          boxHeight: 10,
          generateLabels: (chart) => [{
            text: 'Ventas Mensuales',
            fillStyle: monthlyChartMainColor,
            strokeStyle: 'rgba(0, 0, 0, 0.1)',
            lineWidth: 1,
            hidden: false
          }]
        }
      },
      title: {
        display: true,
        text: `Ventas Mensuales (Mis Ventas)`,
      },
    },
  };

  // Verificar si existen datos de ventas mensuales
  const hasMonthlyData = chartData.monthlyChartData && 
                        chartData.monthlyChartData.labels && 
                        chartData.monthlyChartData.totals;
  
  // Crear datos para la gráfica de ventas mensuales
  const monthlyChartData = {
    labels: hasMonthlyData ? chartData.monthlyChartData.labels : ['Sin datos'],
    datasets: [
      {
        label: 'Ventas Mensuales',
        data: hasMonthlyData ? chartData.monthlyChartData.totals : [0],
        // Usar el color principal para Mayo y colores distintos para los demás meses
        backgroundColor: (context) => {
          // Obtener el índice del mes actual (0-indexed, donde 0 = enero)
          const currentMonthIndex = new Date().getMonth();
          
          // Array de colores para cada mes
          const colors = [
            'rgba(255, 99, 132, 0.7)',   // Enero
            'rgba(54, 162, 235, 0.7)',   // Febrero
            'rgba(255, 206, 86, 0.7)',   // Marzo
            'rgba(75, 192, 192, 0.7)',   // Abril
            monthlyChartMainColor,       // Mayo (color principal)
            'rgba(255, 159, 64, 0.7)',   // Junio
            'rgba(255, 99, 132, 0.7)',   // Julio
            'rgba(54, 162, 235, 0.7)',   // Agosto
            'rgba(255, 206, 86, 0.7)',   // Septiembre
            'rgba(75, 192, 192, 0.7)',   // Octubre
            'rgba(153, 102, 255, 0.7)',  // Noviembre
            'rgba(255, 159, 64, 0.7)'    // Diciembre
          ];
          
          // Si es un contexto de array, devolver todos los colores
          if (Array.isArray(context)) {
            return colors;
          }
          
          // Si es un contexto de índice, devolver el color correspondiente
          const index = context.dataIndex;
          return colors[index];
        },
        borderColor: 'rgba(0, 0, 0, 0.1)',
        borderWidth: 1,
      },
    ],
  };
  
  // Imprimir datos para depuración
  console.log('Datos de la gráfica mensual:', {
    labels: monthlyChartData.labels,
    data: monthlyChartData.datasets[0].data
  });

  return (
    <div className="sales-charts-container">
      <div className="chart-wrapper">
        <h3>Ventas Diarias del Mes</h3>
        <div className="chart-container">
          <Bar options={barChartOptions} data={barChartData} />
        </div>
      </div>
      
      <div className="chart-wrapper">
        <h3>Ventas Mensuales</h3>
        <div className="chart-container">
          <Bar options={monthlyChartOptions} data={monthlyChartData} />
        </div>
      </div>
    </div>
  );
};

export default SellerSalesChart;
